import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  getApiV1TutoraiDocumentsQueryOptions,
  useDeleteApiV1TutoraiDocumentsDocumentid,
  usePostApiV1TutoraiDocumentsDocumentidImport,
} from '@/shared/api/generated';

/** Resultado de uma importação, para a tela reportar o custo ao operador. */
export interface ImportResult {
  readonly chunksGenerated: number;
  readonly embedTokensUsed: number;
}

export interface UseTutorDocumentActionsOptions {
  /** Chamado após uma importação bem-sucedida, com chunks e tokens consumidos. */
  readonly onImported?: (result: ImportResult) => void;
  readonly onImportError?: () => void;
  readonly onDeleted?: () => void;
  readonly onError?: () => void;
}

/**
 * Ações de escrita sobre um documento de contexto: importar e excluir.
 *
 * As duas invalidam a listagem da turma pelo `queryOptions` gerado, porque as duas mudam o
 * status do documento e a UI não deve inferir o resultado — o servidor decide.
 *
 * Nenhuma das duas envia identidade: o backend deriva a autoria do token de acesso. O
 * endpoint de import deixou de aceitar corpo, e o `personName` que aparece na linha do
 * documento vem da resposta do GET, preenchido pelo servidor.
 *
 * @param classStudentId - Turma cuja listagem deve ser invalidada
 * @param options - Callbacks de sucesso e erro por ação
 * @returns As duas ações e se alguma mutation está em andamento
 *
 * @example
 * ```tsx
 * const { importDocument } = useTutorDocumentActions(9020, {
 *   onImported: ({ chunksGenerated }) => alert({ alertType: 'success', title: `${chunksGenerated} chunks` }),
 * });
 * ```
 */
export function useTutorDocumentActions(
  classStudentId: number,
  options: UseTutorDocumentActionsOptions = {},
) {
  const queryClient = useQueryClient();

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries(getApiV1TutoraiDocumentsQueryOptions({ classStudentId }));
  }, [queryClient, classStudentId]);

  const importMutation = usePostApiV1TutoraiDocumentsDocumentidImport({
    mutation: {
      onSuccess: result => {
        invalidate();
        options.onImported?.({
          chunksGenerated: result?.chunksGenerated ?? 0,
          embedTokensUsed: result?.embedTokensUsed ?? 0,
        });
      },
      onError: () => {
        invalidate();
        options.onImportError?.();
      },
    },
  });

  const deleteMutation = useDeleteApiV1TutoraiDocumentsDocumentid({
    mutation: {
      onSuccess: () => {
        invalidate();
        options.onDeleted?.();
      },
      onError: () => {
        invalidate();
        options.onError?.();
      },
    },
  });

  const importDocument = useCallback(
    (documentId: number) => importMutation.mutate({ documentId }),
    [importMutation],
  );

  const deleteDocument = useCallback(
    (documentId: number) => deleteMutation.mutate({ documentId }),
    [deleteMutation],
  );

  return {
    importDocument,
    deleteDocument,
    isMutating: importMutation.isPending || deleteMutation.isPending,
  } as const;
}
